import React, { memo } from "react";
import type { ImgHTMLAttributes, SyntheticEvent } from "react";

import { Box, Portal, SxProps, Theme } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { useLens } from "@/src/components/molecules/Lens/useLens";

export interface LensProps {
  src: string;
  alt?: string;
  zoomFactor?: number;
  lensSize?: number;
  disabled?: boolean;
  zoomSrc?: string;
  rotation?: number;
  sx?: SxProps<Theme>;
  imgProps?: ImgHTMLAttributes<HTMLImageElement>;
  onLoad?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
  onError?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
  onInteractionChange?: (isInteracting: boolean) => void;
}

const Lens = (props: LensProps) => {
  const {
    src,
    alt = "",
    zoomFactor = 3,
    lensSize = 200,
    disabled = false,
    zoomSrc,
    rotation = 0,
    sx,
    imgProps,
    onLoad,
    onError,
    onInteractionChange
  } = props;

  const {
    lensState,
    currentZoom,
    imageRef,
    containerRef,
    handleMouseMove,
    handleMouseLeave,
    handleTouchMove,
    handleTouchStart,
    handleTouchEnd
  } = useLens({
    zoomFactor,
    lensSize,
    disabled,
    rotation,
    onInteractionChange
  });

  const bgWidth = lensState.width * currentZoom;
  const bgHeight = lensState.height * currentZoom;
  const bgPosX = lensSize / 2 - lensState.relX * currentZoom;
  const bgPosY = lensSize / 2 - lensState.relY * currentZoom;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none", // Prevent scrolling while dragging
        ...sx
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <Box
        component="img"
        ref={imageRef}
        src={src}
        alt={alt}
        onLoad={onLoad}
        onError={onError}
        {...imgProps}
        sx={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          borderRadius: "8px",
          userSelect: "none",
          WebkitUserSelect: "none",
          // Prevent callouts/context menu on long press
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
          ...imgProps?.style
        }}
      />

      {!disabled && lensState.visible && (
        <Portal>
          <Box
            sx={{
              position: "fixed",
              width: `${lensSize}px`,
              height: `${lensSize}px`,
              left: `${lensState.x}px`,
              top: `${lensState.y}px`,
              border: "2px solid white",
              overflow: "hidden",
              pointerEvents: "none",
              zIndex: 1500,
              boxShadow: `0 4px 20px ${theme.palette.misc.backdropOverlay}`,
              backgroundColor: theme.palette.background.paper,
              backgroundImage: `url(${zoomSrc ?? src})`,
              backgroundSize: `${bgWidth}px ${bgHeight}px`,
              backgroundPosition: `${bgPosX}px ${bgPosY}px`,
              backgroundRepeat: "no-repeat",
              transform: `rotate(${rotation}deg)`
            }}
          />
        </Portal>
      )}
    </Box>
  );
};

export default memo(Lens);
